import { useEffect } from "react";

import { checkIsOrderContainNonTradeInItems, checkIsTradeInPurchase } from "@jti/commerce/shared";
import type { Order } from "@jti/magento";
import type { CheckoutGTMFlowType, TemplateName } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { buildPurchaseData, buildTradeInPurchaseData, getTemplateName } from "@checkout-v2/shared/utils";
import { getOrderCheckoutFlow, removeOrderCheckoutFlow } from "@checkout-v2/shared/utils/purchaseTrackingStorage";

import { useCustomerOrder } from "../useCustomerOrder";

const checkoutTemplateName: TemplateName = "checkout";
const sharedData = getTemplateName(checkoutTemplateName);

const trackGtmOrderSummaryPurchase = (order: Order, checkoutFlowType: CheckoutGTMFlowType): void => {
  const orderConfirmationTemplate: TemplateName = "order-confirmation";

  dispatchTrackingEvent(TrackingEvents.TrackingCheckoutPageView, {
    section: orderConfirmationTemplate,
    template_name: orderConfirmationTemplate,
    checkout_type: checkoutTemplateName,
  });

  if (checkIsTradeInPurchase(order)) {
    const purchaseData = buildTradeInPurchaseData({ order, sharedData, isTradeInPurchase: true });
    dispatchTrackingEvent(TrackingEvents.TrackingOrderTradeIn, purchaseData);
  }

  if (checkIsOrderContainNonTradeInItems(order)) {
    const purchaseData = buildPurchaseData(order, sharedData);
    dispatchTrackingEvent(TrackingEvents.TrackingOrderSummaryPurchase, {
      ...purchaseData,
      checkout_flow_type: checkoutFlowType,
    });
  }
};

export const useGtmOrderSummaryPurchase = (): void => {
  const { data: order, isLoading, isSuccess } = useCustomerOrder();

  useEffect(() => {
    const checkoutFlowType = order ? getOrderCheckoutFlow(order.number) : undefined;

    if (isLoading || !isSuccess || !order || !checkoutFlowType) {
      return;
    }

    removeOrderCheckoutFlow(order.number);
    trackGtmOrderSummaryPurchase(order, checkoutFlowType);
  }, [isLoading, isSuccess, order]);
};
